import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { competitionIsOpen, competitionStatusLabel } from '../lib/competitionStatus'
import { useCountdown } from '../lib/useCountdown'

type Competition = { id:number, slug:string, title:string, closesAt:string, status:string }
type StartResponse = { ok: true, emailSent: boolean, editUrl?: string, message: string, autoOpenEditor: boolean }

function validateCompetitionNumber(value: string) {
  return /^[NIA][0-9]+$/i.test(value.trim())
}

function validateEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

export default function CompetitionSubmit() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [comp, setComp] = useState<Competition | null>(null)
  const [number, setNumber] = useState('')
  const [email, setEmail] = useState('')
  const [submission, setSubmission] = useState<{ editUrl: string, emailSent: boolean } | null>(null)
  const [postingByKey, setPostingByKey] = useState<Record<string, boolean>>({})
  const [postedByKey, setPostedByKey] = useState<Record<string, boolean>>({})
  const [message, setMessage] = useState('')
  const [err, setErr] = useState('')
  const [startErr, setStartErr] = useState('')
  const [copied, setCopied] = useState(false)
  const postResetTimers = useRef<Record<string, number>>({})

  function setPosting(key: string, value: boolean) {
    setPostingByKey(prev => ({ ...prev, [key]: value }))
  }

  function setPosted(key: string) {
    if (postResetTimers.current[key]) window.clearTimeout(postResetTimers.current[key])
    setPostedByKey(prev => ({ ...prev, [key]: true }))
    postResetTimers.current[key] = window.setTimeout(() => {
      setPostedByKey(prev => ({ ...prev, [key]: false }))
      delete postResetTimers.current[key]
    }, 1500)
  }

  useEffect(() => {
    return () => {
      for (const timer of Object.values(postResetTimers.current)) window.clearTimeout(timer)
    }
  }, [])

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      setStartErr('Could not copy the link.')
    }
  }

  useEffect(() => {
    api<{ competition: Competition }>(`/api/public/competitions/${slug}`)
      .then(d => setComp(d.competition))
      .catch(e => setErr(e.message))
  }, [slug])

  const countdown = useCountdown(comp?.closesAt || null)
  const locked = useMemo(() => comp ? !competitionIsOpen(comp.status, comp.closesAt) : true, [comp])

  async function start() {
    setErr(''); setStartErr(''); setMessage('')
    const competitionNumber = number.trim().toUpperCase()
    const emailValue = email.trim()
    if (!validateCompetitionNumber(competitionNumber)) {
      setStartErr('Competition number must start with N, I, or A and contain digits only (for example: N1, I64, A102).')
      return
    }
    if (!validateEmail(emailValue)) {
      setStartErr('Please enter a valid email address.')
      return
    }
    setPosting('start', true)
    try {
      const [d] = await Promise.all([
        api<StartResponse>(`/api/competitions/${slug}/start`, {
          method: 'POST',
          headers: { 'content-type':'application/json' },
          body: JSON.stringify({ competitionNumber, email: emailValue })
        }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      if (d.autoOpenEditor) {
        if (!d.editUrl) throw new Error('Edit link missing.')
        navigate(new URL(d.editUrl).pathname, {
          state: {
            arrivedFromEntry: true,
            emailSent: d.emailSent,
            submissionEmail: emailValue
          }
        })
        return
      } else {
        setSubmission(d.editUrl ? { editUrl: d.editUrl, emailSent: d.emailSent } : null)
      }
      setMessage(d.message)
      setPosted('start')
    } catch (e:any) {
      setStartErr(e.message)
    } finally {
      setPosting('start', false)
    }
  }

  return (
    <div className="card">
      {err && <p className='error-text'>{err}</p>}
      {!comp ? <p><small className="muted">Loading…</small></p> : (
        <>
          <h2 style={{marginTop:0}}>{comp.title}</h2>
          <div className="row" style={{justifyContent:'space-between'}}>
            <div>
              <div><small className="muted">Closes at: {new Date(comp.closesAt).toLocaleString()}</small></div>
              <div><small className="muted">Time remaining: {countdown.pretty}</small></div>
            </div>
            <span className="badge">{locked ? competitionStatusLabel(comp.status) : 'open'}</span>
          </div>

          <hr />
          <form onSubmit={(e) => { e.preventDefault(); void start() }}>
            <div className="grid2">
              <div>
                <label>Competition number (e.g. N1, I64, A102)</label>
                <input
                  value={number}
                  onChange={e=>setNumber(e.target.value)}
                  placeholder="N1"
                  autoCapitalize="characters"
                />
              </div>
              <div>
                <label>Email (we send your edit link here)</label>
                <input
                  value={email}
                  onChange={e=>setEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                />
              </div>
            </div>

            {startErr && <p className='error-text'>{startErr}</p>}
            <div className="row" style={{marginTop: 12}}>
              <button type="submit" className="primary" disabled={locked || Boolean(postingByKey.start)}>
                {postingByKey.start ? (
                  <span className="row" style={{gap:8}}>
                    <span className="btn-spinner" />
                    <span>Starting...</span>
                  </span>
                ) : postedByKey.start ? 'Saved' : 'Enter Competition'}
              </button>
            </div>
          </form>

          {message && !(submission && !submission.emailSent) && <div className="success-text">{message}</div>}

          {submission && !submission.emailSent && (
            <div className="card" style={{marginTop: 12, background:'#0b1220'}}>
              <div style={{fontWeight:700}}>Email sending is disabled right now. Your personal edit link for the {comp.title} competiton:</div>
              <div className="link-copy-row" style={{marginTop: 8}}>
                <a href={submission.editUrl} style={{wordBreak:'break-all', flex:'1 1 320px'}}>{submission.editUrl}</a>
                <button
                  type="button"
                  className={`icon-button${copied ? ' icon-button-copied' : ''}`}
                  onClick={() => copyLink(submission.editUrl)}
                  aria-label={copied ? 'Link copied' : 'Copy personal edit link'}
                  title={copied ? 'Copied' : 'Copy link'}
                >
                  <span className="copy-icon" aria-hidden="true">
                    <span className="copy-icon-back" />
                    <span className="copy-icon-front" />
                  </span>
                </button>
              </div>
              <p><small className="muted">Keep this as a backup link. Do not share it with anyone else, because anyone with this link can edit your entries.</small></p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
